/**
 * 一覧末尾（追加読み込み領域）の表示状態。
 *
 * `hasMore` / `isLoadingMore` / `loadMoreError` / `isAutoLoadSupported` / `lastLoadAddedNothing` の
 * 5つを画面の JSX で直接組み合わせると、条件が増えるたびに複数箇所を同時に直す必要があり、
 * 抜けると「続きに到達できない」という無症状の詰みになる。
 * 一覧本体の resolveAgentListViewState と同じく、状態をひとつに畳んでから描画する。
 */
export type LoadMoreViewState =
  // 追加読み込みの余地が無い（末尾に何も出さない）
  | "none"
  // 取得中。一覧は残したまま末尾にスピナーを出す
  | "loading"
  // 取得に失敗した。取得済みは残したまま末尾に失敗表示と再試行を出す
  | "error"
  // 自動取得に任せられる。末尾監視用の番兵を置く
  | "sentinel"
  // 自動取得に任せられないので、手動の「続きを読み込む」を出す
  | "manual";

interface ResolveLoadMoreViewStateParams {
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreError: boolean;
  /** IntersectionObserver が使えるか。使えない WebView では番兵が発火しない */
  isAutoLoadSupported: boolean;
  /** 直前の追加読み込みで一覧が1件も増えなかったか（ページ跨ぎで全件が既出だった等） */
  lastLoadAddedNothing: boolean;
}

export const resolveLoadMoreViewState = ({
  hasMore,
  isLoadingMore,
  loadMoreError,
  isAutoLoadSupported,
  lastLoadAddedNothing,
}: ResolveLoadMoreViewStateParams): LoadMoreViewState => {
  if (!hasMore) return "none";
  if (isLoadingMore) return "loading";
  if (loadMoreError) return "error";
  // 一覧が伸びなかったときは番兵の交差状態も変わらず、IntersectionObserver は二度と発火しない。
  // 自動取得に任せると末尾で静かに詰むため、手動導線へ切り替える
  if (!isAutoLoadSupported || lastLoadAddedNothing) return "manual";
  return "sentinel";
};
